import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { PlusCircle, AlertCircle } from 'lucide-react';
import Layout from '@/components/Layout';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { useAuth } from '@/context/AuthContext';
import { useChildren } from '@/hooks/useChildren';
import ChildGrid from '@/components/child/ChildGrid';
import EmptyChildrenState from '@/components/child/EmptyChildrenState';
import AddChildForm from '@/components/child/AddChildForm';
import ChildLoadingSkeleton from '@/components/child/ChildLoadingSkeleton';
import { useFamilyPermissions } from '@/hooks/useFamilyPermissions';
import CreateFamilyPrompt from '@/components/child/CreateFamilyPrompt';
import { logger } from '@/utils/logger';

const Children = () => {
  const { user } = useAuth();
  const { children, isLoading, error, addChild } = useChildren(user?.id);
  const { familyId, isLoading: isFamilyLoading } = useFamilyPermissions();
  const [showCreateFamilyPrompt, setShowCreateFamilyPrompt] = useState(false);
  
  useEffect(() => {
    logger.log("Children page rendered with:", {
      userId: user?.id,
      childrenCount: children.length,
      isLoading,
      error,
      familyId
    });
  }, [user?.id, children.length, isLoading, error, familyId]);
  
  const handleAddChildClick = () => {
    if (!familyId) {
      setShowCreateFamilyPrompt(true);
      return;
    }
  };

  const handleAddChild = async (childData: { 
    name: string; 
    birthdate: string; 
    photo?: File 
  }) => {
    await addChild(childData);
  };
  
  const isPageLoading = isLoading || isFamilyLoading;
  
  return (
    <Layout>
      <div className="pb-16">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Child Profiles</h1>
          {familyId ? (
            <Dialog>
              <DialogTrigger asChild>
                <Button className="flex items-center gap-2">
                  <PlusCircle className="h-4 w-4" />
                  Add Child
                </Button>
              </DialogTrigger>
              <AddChildForm onAddChild={handleAddChild} />
            </Dialog>
          ) : (
            <Button 
              className="flex items-center gap-2"
              onClick={handleAddChildClick}
            >
              <PlusCircle className="h-4 w-4" />
              Add Child
            </Button>
          )}
        </div>
        
        {isPageLoading ? (
          <div>
            <p className="text-muted-foreground mb-4">Loading child profiles...</p>
            <ChildGrid children={[]} isLoading={true} />
          </div>
        ) : error ? (
          <div className="border border-red-200 bg-red-50 rounded-lg p-6 text-center">
            <AlertCircle className="h-10 w-10 text-red-500 mx-auto mb-2" />
            <p className="text-red-600 mb-3">{error}</p>
            <p className="text-sm text-red-500 mb-3">
              There might be an issue connecting to the database or your session may have expired.
            </p>
          </div>
        ) : !familyId ? (
          <CreateFamilyPrompt 
            open={showCreateFamilyPrompt} 
            onOpenChange={setShowCreateFamilyPrompt} 
          />
        ) : children.length > 0 ? (
          <ChildGrid children={children} />
        ) : (
          <EmptyChildrenState onAddChild={addChild} />
        )}
      </div>
    </Layout>
  );
};

export default Children;
